/**
 * Metadata readers and the axes-alignment upload path.
 *
 * The per-step mesh uploads themselves live in `stepCheckpointUpload.js`, which
 * drives every step off one table; this module keeps the tool-state readers that
 * table depends on.
 */
import { exportMesh } from './exporters/modelExporter.js';
import { EXPORT_FORMATS } from './exportConfig.js';
import { uploadScanFinalPreviewCheckpoint } from '../services/erpApiService.js';
import { TOOLS_CONFIG } from '../constants/tools.js';
import { LOGO_PLACEMENT_DEFAULTS } from '../constants/logoPlacementDefaults.js';
import { isBulldogKind } from '../services/operations/locking/common/lockingPipelineConstants.js';
import useEditorStore from '../stores/useEditorStore.js';
import useLockingSystemStore from '../stores/useLockingSystemStore.js';
import useValveStore from '../stores/useValveStore.js';

const LOGO_TOOL_ID = TOOLS_CONFIG.MAIN_PROCESS.LOGO_PLACEMENT.main_logo_placement.id;

export function readLogoPlacementMetadata() {
  const raw = useEditorStore.getState().toolSettings[LOGO_TOOL_ID];
  return {
    textureScale:
      typeof raw?.textureScale === 'number' ? raw.textureScale : LOGO_PLACEMENT_DEFAULTS.textureScale,
    offsetX: typeof raw?.offsetX === 'number' ? raw.offsetX : LOGO_PLACEMENT_DEFAULTS.offsetX,
    offsetY: typeof raw?.offsetY === 'number' ? raw.offsetY : LOGO_PLACEMENT_DEFAULTS.offsetY,
    patchHalfSizeCm:
      typeof raw?.patchHalfSizeCm === 'number'
        ? raw.patchHalfSizeCm
        : LOGO_PLACEMENT_DEFAULTS.patchHalfSizeCm,
    rotateDeg: typeof raw?.rotateDeg === 'number' ? raw.rotateDeg : LOGO_PLACEMENT_DEFAULTS.rotateDeg,
    embossEnabled:
      typeof raw?.embossEnabled === 'boolean'
        ? raw.embossEnabled
        : LOGO_PLACEMENT_DEFAULTS.embossEnabled,
    embossDepthMm:
      typeof raw?.embossDepthMm === 'number' ? raw.embossDepthMm : LOGO_PLACEMENT_DEFAULTS.embossDepthMm,
    maxSubdivideIterations:
      typeof raw?.maxSubdivideIterations === 'number'
        ? raw.maxSubdivideIterations
        : LOGO_PLACEMENT_DEFAULTS.maxSubdivideIterations,
  };
}

/**
 * `plain` unless a Bull Dog attachment command is current head of editor
 * history. Every Bull Dog variant (V1, V2) shares the `bulldog` slot — they are
 * the same attachment at different engulf heights.
 */
export function resolveLockingCheckpointVariant() {
  const cmd = useLockingSystemStore.getState().currentAttachmentCommand;
  if (!cmd) return 'plain';
  const { history, historyIndex } = useEditorStore.getState();
  const atHead = historyIndex >= 0 && history[historyIndex] === cmd;
  if (atHead && isBulldogKind(cmd.attachmentKind)) {
    return 'bulldog';
  }
  return 'plain';
}

/**
 * Clinical checkpoint file: single ASCII PLY (vertex colors when present).
 *
 * @param {import('three').Mesh} mesh
 * @param {string} stem - filename stem without extension
 */
function plyCheckpointFile(mesh, stem) {
  const blob = exportMesh(mesh, {
    format: EXPORT_FORMATS.PLY,
    preserveColors: true,
  });
  return new File([blob], `${stem}.ply`, { type: 'text/plain;charset=utf-8' });
}

/**
 * Applied valve configuration for the valve checkpoint metadata (JSON-safe).
 * THREE.Vector3 fields inside `valveHole` serialize to `{x, y, z}` via JSON clone.
 */
export function readValveCheckpointMetadata() {
  const s = useValveStore.getState();
  const safeClone = (v) => {
    try {
      return v == null ? null : JSON.parse(JSON.stringify(v));
    } catch {
      return null;
    }
  };
  return {
    selectedValveId: s.selectedValveId ?? null,
    valvePlaced: Boolean(s.valvePlaced),
    valveApplied: Boolean(s.valveApplied),
    clearance: safeClone(s.valve),
    valveHole: safeClone(s.valveHole),
  };
}

/**
 * Scan final preview checkpoint: same PLY export as rectification (vertex colors when present).
 *
 * This is the axes-alignment step's checkpoint. It keeps its own upload path
 * (rather than going through `uploadStepCheckpoint`) because the caller in
 * `useWorkflowStore` waits for patient/scan hydration and guards against
 * duplicate persists on deep links. `metadata` is the standard step envelope,
 * passed in by the caller to avoid an import cycle.
 */
export async function uploadScanFinalPreviewCheckpointFromEditor(
  shadedMesh,
  salesOrderId,
  customer,
  metadata,
) {
  const file = plyCheckpointFile(shadedMesh, 'scan_final_preview_checkpoint');
  return uploadScanFinalPreviewCheckpoint(salesOrderId, customer, file, metadata);
}
